/**
 * PlotShareModal — FXC-8723 share-creation flow for the authenticated presenter.
 *
 * Opens from the plot toolbar's "Share / QR" button. Flow:
 *   1. Presenter picks which flowfield points to include (all = null, or a subset).
 *   2. Optionally sets a human label and expiry.
 *   3. Clicks "Create share link" → calls mintPlotShareToken.
 *   4. On success: shows QR code + share_url with copy & download controls.
 *   5. Fire-and-forgets triggerFlowfieldPreBake for FXC-8721 pre-bake.
 *
 * Responsibilities of this component:
 *   - Mint (step 2 → mintPlotShareToken)
 *   - Pre-bake trigger (fire-and-forget → triggerFlowfieldPreBake)
 *   - QR generation (step 4 → QRCode canvas component)
 *
 * NOT touched:
 *   - PlotShareView (anonymous viewer — FXC-8724)
 *   - plot_share.py backend
 */

import { useCallback, useRef, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Callout,
  Checkbox,
  Dialog,
  Flex,
  ScrollArea,
  Separator,
  Text,
  TextField,
  Tooltip,
} from "@radix-ui/themes";

import {
  mintPlotShareToken,
  triggerFlowfieldPreBake,
  type PlotShareMintResponse,
} from "../../api/plotShare";
import type { SavedPlot } from "../../api/plots";
import QRCodeCanvas, { type QRCodeHandle } from "../qr/QRCode";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface PlotShareModalProps {
  plot: SavedPlot;
  projectId: string;
  branch: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type ModalStep = "configure" | "minting" | "success" | "error";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const EXPIRY_OPTIONS = [
  { label: "24 hours", hours: 24 },
  { label: "3 days", hours: 72 },
  { label: "7 days", hours: 168 },
  { label: "30 days", hours: 720 },
  { label: "1 year", hours: 8760 },
] as const;

const DEFAULT_EXPIRY_HOURS = 72;

function extractFlowfieldPoints(plot: SavedPlot): string[] {
  // Attempt to pull flowfield point IDs from the plot's source_node_ids.
  // These are the "allowed_points" candidates the presenter can include or
  // exclude when sharing. Falls back to source_node_ids if no explicit set.
  const meta = plot.metadata ?? {};
  const explicit = meta["flowfield_point_ids"];
  if (Array.isArray(explicit) && explicit.length > 0) {
    return explicit as string[];
  }
  return plot.source_node_ids ?? [];
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function PlotShareModal({
  plot,
  projectId,
  branch,
  open,
  onOpenChange,
}: PlotShareModalProps) {
  const availablePoints = extractFlowfieldPoints(plot);

  const [step, setStep] = useState<ModalStep>("configure");
  const [label, setLabel] = useState("");
  const [expiryHours, setExpiryHours] = useState<number>(DEFAULT_EXPIRY_HOURS);
  const [selectedPoints, setSelectedPoints] = useState<Set<string>>(
    new Set(availablePoints),
  );
  const [allPoints, setAllPoints] = useState(true);
  // Flowfield is a heavy data pull; default OFF so anonymous viewers can't
  // fetch flowfield data unless the presenter explicitly opts in.
  const [flowfieldEnabled, setFlowfieldEnabled] = useState(false);
  const [result, setResult] = useState<PlotShareMintResponse | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [preBakeStatus, setPreBakeStatus] = useState<
    "pending" | "accepted" | "unavailable" | null
  >(null);

  const qrRef = useRef<QRCodeHandle>(null);

  const reset = useCallback(() => {
    setStep("configure");
    setLabel("");
    setExpiryHours(DEFAULT_EXPIRY_HOURS);
    setSelectedPoints(new Set(availablePoints));
    setAllPoints(true);
    setResult(null);
    setErrorMsg(null);
    setCopied(false);
    setPreBakeStatus(null);
  }, [availablePoints]);

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next) reset();
      onOpenChange(next);
    },
    [onOpenChange, reset],
  );

  const togglePoint = useCallback((pointId: string) => {
    setSelectedPoints((prev) => {
      const next = new Set(prev);
      if (next.has(pointId)) next.delete(pointId);
      else next.add(pointId);
      return next;
    });
  }, []);

  const handleToggleAll = useCallback(
    (checked: boolean) => {
      setAllPoints(checked);
      if (checked) setSelectedPoints(new Set(availablePoints));
      else setSelectedPoints(new Set());
    },
    [availablePoints],
  );

  const handleMint = useCallback(async () => {
    setStep("minting");
    setErrorMsg(null);

    const allowedPoints =
      allPoints || availablePoints.length === 0
        ? null
        : Array.from(selectedPoints);

    try {
      const minted = await mintPlotShareToken({
        plot_id: plot.id,
        project_id: projectId,
        branch,
        allowed_points: allowedPoints,
        expires_in_hours: expiryHours,
        label: label.trim() || undefined,
        flowfield_enabled: flowfieldEnabled,
      });
      setResult(minted);
      setStep("success");

      // Only pre-bake (a heavy Flow360 pull) when the share actually grants
      // flowfield access — the serve + prebake endpoints are both gated on it.
      if (flowfieldEnabled) {
        setPreBakeStatus("pending");
        triggerFlowfieldPreBake(plot.id, minted.token_prefix, allowedPoints).then(
          ({ accepted }) => {
            setPreBakeStatus(accepted ? "accepted" : "unavailable");
          },
        );
      } else {
        setPreBakeStatus(null);
      }
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : "Failed to create share link.";
      setErrorMsg(msg);
      setStep("error");
    }
  }, [
    allPoints,
    availablePoints.length,
    branch,
    expiryHours,
    flowfieldEnabled,
    label,
    plot.id,
    projectId,
    selectedPoints,
  ]);

  const handleCopy = useCallback(() => {
    if (!result?.share_url) return;
    navigator.clipboard.writeText(result.share_url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  }, [result?.share_url]);

  const handleDownloadQR = useCallback(() => {
    const slug = (label.trim() || plot.title || "plot-share")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .slice(0, 40);
    qrRef.current?.download(`${slug}-qr.png`);
  }, [label, plot.title]);

  // ------------------------------------------------------------------
  // Configure step
  // ------------------------------------------------------------------

  const configureStep = (
    <Box style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <Box>
        <Text size="2" weight="medium" mb="1" as="p">
          Plot
        </Text>
        <Text size="2" color="gray" as="p">
          {plot.title}
        </Text>
      </Box>

      <Separator size="4" />

      {/* Label */}
      <Box>
        <Text size="2" weight="medium" mb="1" as="p">
          Label <Text size="1" color="gray">(optional)</Text>
        </Text>
        <TextField.Root
          placeholder="e.g. AIAA 2026 slides"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          maxLength={255}
          data-testid="share-label-input"
        />
      </Box>

      {/* Expiry */}
      <Box>
        <Text size="2" weight="medium" mb="1" as="p">
          Expires
        </Text>
        <Flex gap="2" wrap="wrap">
          {EXPIRY_OPTIONS.map((opt) => (
            <Button
              key={opt.hours}
              size="1"
              variant={expiryHours === opt.hours ? "solid" : "soft"}
              color={expiryHours === opt.hours ? "blue" : "gray"}
              onClick={() => setExpiryHours(opt.hours)}
              data-testid={`expiry-${opt.hours}`}
            >
              {opt.label}
            </Button>
          ))}
        </Flex>
      </Box>

      {/* Flowfield access — opt-in (heavy data pull, off by default) */}
      <Separator size="4" />
      <Box>
        <Flex align="center" gap="2" mb="1">
          <Checkbox
            checked={flowfieldEnabled}
            onCheckedChange={(val) => setFlowfieldEnabled(Boolean(val))}
            data-testid="share-flowfield-toggle"
          />
          <Text size="2" weight="medium">
            Allow flowfield access
          </Text>
        </Flex>
        <Text size="1" color="gray" as="p">
          Off by default. When enabled, viewers of this link can fetch the
          plot's flowfield data — a heavy pull from the simulation backend.
          Leave off for public/QR shares.
        </Text>
      </Box>

      {/* Flowfield points selection — only when flowfield access is granted */}
      {flowfieldEnabled && availablePoints.length > 0 && (
        <>
          <Separator size="4" />
          <Box>
            <Flex align="center" gap="2" mb="2">
              <Text size="2" weight="medium">
                Flowfield points to include
              </Text>
              <Badge color="gray" size="1">
                {allPoints ? "all" : `${selectedPoints.size} / ${availablePoints.length}`}
              </Badge>
            </Flex>
            <Text size="1" color="gray" mb="3" as="p">
              Uncheck points to restrict which flowfield slices the viewer can
              access. Requires FXC-8721 pre-bake to be effective.
            </Text>

            {/* All-points toggle */}
            <Flex align="center" gap="2" mb="2">
              <Checkbox
                checked={allPoints}
                onCheckedChange={(val) => handleToggleAll(Boolean(val))}
                data-testid="share-all-points-toggle"
              />
              <Text size="2">Include all points</Text>
            </Flex>

            {/* Per-point list */}
            {!allPoints && (
              <ScrollArea
                style={{ maxHeight: 180, border: "1px solid var(--gray-5)", borderRadius: 6, padding: 8 }}
              >
                <Flex direction="column" gap="1">
                  {availablePoints.map((pt) => (
                    <Flex key={pt} align="center" gap="2">
                      <Checkbox
                        checked={selectedPoints.has(pt)}
                        onCheckedChange={() => togglePoint(pt)}
                        data-testid={`share-point-${pt}`}
                      />
                      <Text size="1" style={{ fontFamily: "monospace" }}>
                        {pt}
                      </Text>
                    </Flex>
                  ))}
                </Flex>
              </ScrollArea>
            )}
          </Box>
        </>
      )}

      {/* Pre-bake dependency note — only relevant when flowfield is enabled */}
      {flowfieldEnabled && (
        <Callout.Root color="blue" size="1">
          <Callout.Text>
            Flowfield slices are pre-baked in the background when the link is
            created. This depends on FXC-8721 (flowfield pre-bake) which may not
            yet be deployed.
          </Callout.Text>
        </Callout.Root>
      )}
    </Box>
  );

  // ------------------------------------------------------------------
  // Success step
  // ------------------------------------------------------------------

  const successStep = result && (
    <Box style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* QR code */}
      <Flex direction="column" align="center" gap="3">
        <QRCodeCanvas
          ref={qrRef}
          value={result.share_url}
          size={220}
          errorCorrectionLevel="M"
        />
        <Button
          size="2"
          variant="soft"
          color="gray"
          onClick={handleDownloadQR}
          data-testid="download-qr-btn"
        >
          <svg width="14" height="14" viewBox="0 0 15 15" fill="currentColor">
            <path d="M7.5 1a.5.5 0 0 1 .5.5v8.793l2.146-2.147a.5.5 0 0 1 .708.708l-3 3a.5.5 0 0 1-.708 0l-3-3a.5.5 0 1 1 .708-.708L7 10.293V1.5a.5.5 0 0 1 .5-.5ZM1 13.5a.5.5 0 0 1 .5-.5h12a.5.5 0 0 1 0 1h-12a.5.5 0 0 1-.5-.5Z" />
          </svg>
          Download PNG for slides
        </Button>
      </Flex>

      <Separator size="4" />

      {/* Share URL */}
      <Box>
        <Text size="2" weight="medium" mb="2" as="p">
          Share URL
        </Text>
        <Flex gap="2" align="center">
          <Box
            style={{
              flex: 1,
              background: "var(--gray-2)",
              borderRadius: 6,
              padding: "6px 10px",
              fontFamily: "monospace",
              fontSize: 11,
              wordBreak: "break-all",
              color: "var(--gray-11)",
            }}
            data-testid="share-url-display"
          >
            {result.share_url}
          </Box>
          <Tooltip content={copied ? "Copied!" : "Copy URL"}>
            <Button
              size="2"
              variant="soft"
              color={copied ? "green" : "gray"}
              onClick={handleCopy}
              data-testid="copy-url-btn"
            >
              {copied ? (
                <svg width="14" height="14" viewBox="0 0 15 15" fill="currentColor">
                  <path d="M7.5 0a7.5 7.5 0 1 0 0 15 7.5 7.5 0 0 0 0-15Zm3.354 5.854-4 4a.5.5 0 0 1-.708 0l-2-2a.5.5 0 1 1 .708-.708L6.5 8.793l3.646-3.647a.5.5 0 1 1 .708.708Z" />
                </svg>
              ) : (
                <svg width="14" height="14" viewBox="0 0 15 15" fill="currentColor">
                  <path d="M5 2a1 1 0 0 0-1 1v1H3a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V9h1a1 1 0 0 0 1-1V3a1 1 0 0 0-1-1H5Zm7 6h-1V3H5v1h5a1 1 0 0 1 1 1v4Zm-9 4V5h6v7H3Z" />
                </svg>
              )}
            </Button>
          </Tooltip>
        </Flex>
      </Box>

      {/* Metadata */}
      <Flex gap="3" wrap="wrap">
        {result.scope.allowed_points && (
          <Badge color="blue" size="1">
            {result.scope.allowed_points.length} point
            {result.scope.allowed_points.length !== 1 ? "s" : ""}
          </Badge>
        )}
        {!result.scope.allowed_points && (
          <Badge color="gray" size="1">
            All flowfield points
          </Badge>
        )}
        {result.expires_at && (
          <Badge color="amber" size="1">
            Expires {new Date(result.expires_at).toLocaleDateString()}
          </Badge>
        )}
        <Badge color="gray" size="1" style={{ fontFamily: "monospace" }}>
          {result.token_prefix}&hellip;
        </Badge>
      </Flex>

      {/* Pre-bake status */}
      {preBakeStatus === "accepted" && (
        <Callout.Root color="green" size="1">
          <Callout.Text>
            Flowfield pre-bake queued — slices will be ready when the viewer
            opens the link.
          </Callout.Text>
        </Callout.Root>
      )}
      {preBakeStatus === "unavailable" && (
        <Callout.Root color="gray" size="1">
          <Callout.Text>
            Flowfield pre-bake (FXC-8721) not yet deployed — slices will be
            fetched on-demand when the viewer first accesses the link.
          </Callout.Text>
        </Callout.Root>
      )}
    </Box>
  );

  // ------------------------------------------------------------------
  // Error step
  // ------------------------------------------------------------------

  const errorStep = (
    <Box style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <Callout.Root color="red">
        <Callout.Text>
          {errorMsg ?? "An unknown error occurred while creating the share link."}
        </Callout.Text>
      </Callout.Root>
      <Button
        variant="soft"
        onClick={() => {
          setStep("configure");
          setErrorMsg(null);
        }}
        data-testid="retry-mint-btn"
      >
        Try again
      </Button>
    </Box>
  );

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Content
        maxWidth="520px"
        style={{ overflow: "visible" }}
        data-testid="plot-share-modal"
      >
        <Dialog.Title>
          <Flex align="center" gap="2">
            <svg width="16" height="16" viewBox="0 0 15 15" fill="none">
              <path
                d="M9.5 2a2.5 2.5 0 1 1 0 5A2.5 2.5 0 0 1 9.5 2Zm0 8a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5ZM3 7.5a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0Zm4.354-2.146a.5.5 0 0 0-.708.708l2.5 2.5a.5.5 0 0 0 .708 0l2.5-2.5a.5.5 0 1 0-.708-.708L9.5 9.293 7.354 5.354ZM7.5 7.793 5.354 5.646a.5.5 0 0 0-.708.708l2.5 2.5a.5.5 0 0 0 .708 0l.146-.146V12.5a.5.5 0 0 0 1 0V8.707l.146.147a.5.5 0 0 0 .708 0l2.5-2.5a.5.5 0 1 0-.708-.708L9.5 8.293 7.5 7.793Z"
                fill="currentColor"
              />
            </svg>
            Share Plot / QR
          </Flex>
        </Dialog.Title>

        <Box mt="4">
          {step === "configure" && configureStep}
          {step === "minting" && (
            <Flex align="center" justify="center" p="6">
              <Text size="2" color="gray">
                Creating share link&hellip;
              </Text>
            </Flex>
          )}
          {step === "success" && successStep}
          {step === "error" && errorStep}
        </Box>

        <Flex mt="5" gap="3" justify="end">
          {step === "configure" && (
            <>
              <Dialog.Close>
                <Button variant="soft" color="gray" data-testid="cancel-share-btn">
                  Cancel
                </Button>
              </Dialog.Close>
              <Button
                onClick={handleMint}
                disabled={
                  !allPoints &&
                  availablePoints.length > 0 &&
                  selectedPoints.size === 0
                }
                data-testid="create-share-btn"
              >
                Create share link
              </Button>
            </>
          )}
          {(step === "success" || step === "error") && (
            <Dialog.Close>
              <Button variant="soft" color="gray" data-testid="close-share-btn">
                Close
              </Button>
            </Dialog.Close>
          )}
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
}
